import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "checkbox",
    "selectAllCheckbox",
    "actions",
    "actionLink",
    "activationButton",
    "link",
    "tableRow",
  ];
  #formData = new FormData();

  #classNames = {
    checkboxIndeterminate: "puts-checkbox--indeterminate",
  };
  #selectors = {
    checkbox: ".puts-checkbox__input",
  };
  #isActivated = false;

  /*
   * Lifecycle methods
   */

  connect() {
    document.addEventListener("planima:DOMUpdated", this.eventDomUpdated);
  }

  disconnect() {
    document.removeEventListener("planima:DOMUpdated", this.eventDomUpdated);
  }

  formTargetConnected() {
    this.#updateDomFromUrl();
  }

  selectAllCheckboxTargetConnected(item) {
    const checkbox = item.querySelector(this.#selectors.checkbox);
    if (!checkbox) {
      return;
    }
    checkbox.addEventListener("change", this.eventSelectAll);
  }

  selectAllCheckboxTargetDisconnected(item) {
    const checkbox = item.querySelector(this.#selectors.checkbox);
    if (!checkbox) {
      return;
    }
    checkbox.removeEventListener("change", this.eventSelectAll);
  }

  checkboxTargetConnected(item) {
    const checkbox = item.querySelector(this.#selectors.checkbox);
    if (!checkbox) {
      return;
    }
    checkbox.addEventListener("change", this.eventCheckItem);
  }

  checkboxTargetDisconnected(item) {
    const checkbox = item.querySelector(this.#selectors.checkbox);
    if (!checkbox) {
      return;
    }
    checkbox.removeEventListener("change", this.eventCheckItem);
  }

  activationButtonTargetConnected(button) {
    button.addEventListener("click", this.eventActivationButton);
  }

  activationButtonTargetDisconnected(button) {
    button.removeEventListener("click", this.eventActivationButton);
  }

  /*
   * Public API
   */

  deselectAll() {
    this.#formData.delete("selected_ids[]");
    this.#updateCheckboxState();
    this.#updateUrl();
  }

  /*
   * Private methods
   */

  #selectId(id) {
    this.#formData.append("selected_ids[]", id);
  }

  #deselectId(id) {
    const values = this.#getSelectedIds().filter((value) => value !== id);
    this.#formData.delete("selected_ids[]");
    values.forEach((value) => {
      this.#formData.append("selected_ids[]", value);
    });
  }

  #updateActionTargetState() {
    this.actionsTargets.forEach((actions) => {
      actions.hidden = !this.#isActivated;
    });

    if (this.#isActivated) {
      this.element.dataset.massSelectionIsActivated = "true";
    } else {
      delete this.element.dataset.massSelectionIsActivated;
    }

    const anySelected = this.#getSelectedIds().length > 0;

    this.actionLinkTargets.forEach((link) => {
      if (anySelected) {
        link.removeAttribute("disabled");
        link.classList.remove("disabled-link");
      } else {
        link.setAttribute("disabled", "disabled");
        link.classList.add("disabled-link");
      }
    });
  }

  #updateCheckboxState() {
    const selectedIds = this.#getSelectedIds();
    this.#isActivated = selectedIds.length > 0;

    this.checkboxTargets.forEach((item) => {
      const checkbox = item.querySelector(this.#selectors.checkbox);
      checkbox.checked = selectedIds.includes(checkbox.value);
    });

    this.selectAllCheckboxTargets.forEach((item) => {
      const checkbox = item.querySelector(this.#selectors.checkbox);
      item.classList.remove(this.#classNames.checkboxIndeterminate);

      const ids = checkbox.getAttribute("data-select-ids").split(",");

      checkbox.checked = ids.every((id) => selectedIds.includes(id));

      if (ids.some((id) => selectedIds.includes(id))) {
        item.classList.add(this.#classNames.checkboxIndeterminate);
      }
    });

    this.#updateActionTargetState();
  }

  #updateUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete("selected_ids");
    url.searchParams.append("selected_ids", this.#getSelectedIds());
    history.replaceState(null, "", url);

    this.#updateLinks();
  }

  #updateLinks() {
    [...this.linkTargets, ...this.actionLinkTargets].forEach((link) => {
      const linkUrl = new URL(link.href);
      linkUrl.searchParams.delete("selected_ids");
      linkUrl.searchParams.append("selected_ids", this.#getSelectedIds());
      link.href = linkUrl.href;
    });
  }

  #updateDomFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedIds = (urlParams.get("selected_ids") ?? "")
      .split(",")
      .filter(Boolean);

    this.#formData.delete("selected_ids[]");
    selectedIds.forEach((id) => {
      this.#formData.append("selected_ids[]", id);
    });

    this.#updateCheckboxState();
    this.#updateLinks();
  }

  #getSelectedIds() {
    return this.#formData.getAll("selected_ids[]").filter(Boolean);
  }

  /*
   * Event handlers
   */

  eventSelectAll = (event) => {
    const isChecked = event.target.checked;
    const ids = event.target.getAttribute("data-select-ids").split(",");

    ids.forEach((id) =>
      isChecked ? this.#selectId(id) : this.#deselectId(id)
    );

    this.#updateCheckboxState();
    this.#updateUrl();
  };

  eventCheckItem = (event) => {
    const item = event.target;
    const isChecked = item.checked;

    if (isChecked) {
      this.#selectId(item.value);
    } else {
      this.#deselectId(item.value);
    }

    this.#updateCheckboxState();
    this.#updateUrl();
  };

  eventActivationButton = () => {
    this.#isActivated = !this.#isActivated;
    this.#updateActionTargetState();
  };

  eventDomUpdated = () => {
    this.#updateDomFromUrl();
  };
}
